// You can write more code here

/* START OF COMPILED CODE */

import BaseScene from "../core/scenes/BaseScene";
import BtnPause from "../core/prefabs/BtnPause";
import UIPositionContainer from "../core/components/UIPositionContainer";
import BtnAudio from "../core/prefabs/BtnAudio";
import BtnBack from "../core/prefabs/BtnBack";
import BtnHome from "../prefabs/BtnHome";
import ScoreTracker from "../prefabs/ScoreTracker";
import PowerTracker from "../prefabs/PowerTracker";
import EndScreen from "../prefabs/EndScreen";
import PauseMenu from "../prefabs/PauseMenu";
/* START-USER-IMPORTS */
import UIButton from '../core/components/UIButton';
import Game from './Game';
import { GameSceneData, SceneData, SceneId, TransitionPoint } from '../app-routing';
import { app } from '../core/App';
import { RunSettings } from '../game/RunSettingsHandler';
import { LevelName, LevelNameValues } from '../game/level/levels/level';
import { RunManager } from '../game/RunManager';
/* END-USER-IMPORTS */

export default class GameUI extends BaseScene {

  constructor() {
    super("GameUI");

    /* START-USER-CTR-CODE */
    this.isPausingOrResuming = false;
    /* END-USER-CTR-CODE */
  }

  editorCreate(): void {

    // btnPause
    const btnPause = new BtnPause(this, 1835.5, 84.5);
    this.add.existing(btnPause);

    // btnAudio
    const btnAudio = new BtnAudio(this, 1845, 74.5);
    this.add.existing(btnAudio);
    btnAudio.visible = false;

    // btnBack
    const btnBack = new BtnBack(this, 84.5, 84.5);
    this.add.existing(btnBack);

    // btnHome
    const btnHome = new BtnHome(this, 84.5, 84.5);
    this.add.existing(btnHome);
    btnHome.visible = false;

    // scoreTracker
    const scoreTracker = new ScoreTracker(this, 800, 73.5);
    this.add.existing(scoreTracker);

    // powerTracker
    const powerTracker = new PowerTracker(this, 1120, 72.5);
    this.add.existing(powerTracker);

    // endScreen
    const endScreen = new EndScreen(this, 960, 382);
    this.add.existing(endScreen);
    endScreen.visible = false;

    // pauseMenu
    const pauseMenu = new PauseMenu(this, 960, 382);
    this.add.existing(pauseMenu);
    pauseMenu.visible = false;

    // btnPause (components)
    const btnPauseUIPositionContainer = new UIPositionContainer(btnPause);
    btnPauseUIPositionContainer.offsetX = 24;
    btnPauseUIPositionContainer.offsetY = 24;
    btnPauseUIPositionContainer.PinRight = true;
    btnPauseUIPositionContainer.PinTop = true;

    // btnAudio (components)
    const btnAudioUIPositionContainer = new UIPositionContainer(btnAudio);
    btnAudioUIPositionContainer.offsetX = 24;
    btnAudioUIPositionContainer.offsetY = 24;
    btnAudioUIPositionContainer.PinRight = true;
    btnAudioUIPositionContainer.PinTop = true;

    // btnBack (components)
    const btnBackUIPositionContainer = new UIPositionContainer(btnBack);
    btnBackUIPositionContainer.offsetX = 24;
    btnBackUIPositionContainer.offsetY = 24;
    btnBackUIPositionContainer.PinLeft = true;
    btnBackUIPositionContainer.PinTop = true;

    // btnHome (components)
    const btnHomeUIPositionContainer = new UIPositionContainer(btnHome);
    btnHomeUIPositionContainer.offsetX = 24;
    btnHomeUIPositionContainer.offsetY = 24;
    btnHomeUIPositionContainer.PinLeft = true;
    btnHomeUIPositionContainer.PinTop = true;

    this.btnPause = btnPause;
    this.btnAudio = btnAudio;
    this.btnBack = btnBack;
    this.btnHome = btnHome;
    this.scoreTracker = scoreTracker;
    this.powerTracker = powerTracker;
    this.endScreen = endScreen;
    this.pauseMenu = pauseMenu;

    this.events.emit("scene-awake");
  }

  private btnPause!: BtnPause;
  private btnAudio!: BtnAudio;
  private btnBack!: BtnBack;
  private btnHome!: BtnHome;
  private scoreTracker!: ScoreTracker;
  private powerTracker!: PowerTracker;
  private endScreen!: EndScreen;
  private pauseMenu!: PauseMenu;

  /* START-USER-CODE */

  public static events = {
    Back: 'back',
    Home: 'home',
    Replay: 'replay',
    Pause: 'pause',
    LevelSelect: 'levelSelect',
    NextLevel: 'nextLevel'
  } as const;

  private sceneData!: SceneData;
  private gameSceneData!: GameSceneData;
  private runSettings!: RunSettings;
  private gameScene!: Game;
  private topBtnStartY: number | undefined;
  private bottomBtnStartY: number | undefined;
  private isPausingOrResuming: boolean;

  public init(data: SceneData) {
    let gameSceneDataDefaults: GameSceneData = {
      shouldSkip: false
    };

    let runSettingsDefaults: RunSettings = {
      selectedLevel: LevelName.Level1,
      instantRestart: false,
      regenOnRestart: true,
      chargesToCollect: 3
    };

    this.sceneData = data;
    this.gameSceneData = Object.assign({}, gameSceneDataDefaults, data.gameSceneData);
    this.runSettings = Object.assign({}, runSettingsDefaults, data.runSettings);
  }

  public create() {
    super.create();
    this.editorCreate();

    this.gameScene = this.scene.get(SceneId.Game) as Game;
    this.events.on(GameUI.events.Home, this.onHome, this);
    this.events.on(GameUI.events.LevelSelect, this.onLevelSelect, this);
    this.events.on(GameUI.events.Replay, this.onReplay, this);
    this.events.on(GameUI.events.NextLevel, this.onNextLevel, this);

    this.btnBack.on(UIButton.events.clicked, this.gameScene.back, this.gameScene);
    this.btnPause.on(UIButton.events.clicked, this.onPause, this);

    this.bindGameEvents();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    // this.hideUI();
    this.hideEndUI();

    this.gameScene.HasGameUICreateFinished = true;
    this.gameScene.start();
  }

  public showGameUI() {
    this.tweens.killTweensOf(this.btnBack);
    this.tweens.killTweensOf(this.btnPause);
    this.tweens.killTweensOf(this.scoreTracker);
    this.tweens.killTweensOf(this.powerTracker);

    this.btnBack.visible = true;
    this.btnPause.visible = true;
    this.scoreTracker.visible = true;
    this.powerTracker.visible = true;
    this.btnBack.alpha = 0;
    this.btnPause.alpha = 0;
    this.scoreTracker.alpha = 0;
    this.powerTracker.alpha = 0;

    this.add.tween({
      targets: [this.btnBack, this.btnPause, this.scoreTracker, this.powerTracker],
      alpha: 1,
      duration: 500,
      ease: Phaser.Math.Easing.Cubic.Out
    });
  }

  public hideGameUI() {
    this.add.tween({
      targets: [this.btnBack, this.btnPause, this.scoreTracker, this.powerTracker],
      alpha: 0,
      duration: 250,
      ease: Phaser.Math.Easing.Cubic.Out,
      onComplete: () => {
        this.btnBack.visible = false;
        this.btnPause.visible = false;
      }
    });
  }

  public showEndUI() {
    this.btnHome.visible = true;
    this.btnHome.alpha = 0;
    this.btnAudio.visible = true;
    this.btnAudio.alpha = 0;

    this.add.tween({
      targets: [this.btnHome, this.btnAudio],
      alpha: 1,
      duration: 500,
      ease: Phaser.Math.Easing.Cubic.Out
    });
  }

  public hideEndUI() {
    this.add.tween({
      targets: [this.btnHome, this.btnAudio],
      alpha: 0,
      duration: 250,
      ease: Phaser.Math.Easing.Cubic.Out,
      onComplete: () => {
        this.btnHome.visible = false;
        this.btnAudio.visible = false;
      }
    });
  }

  public hideAudioBtn() {
    this.add.tween({
      targets: [this.btnAudio],
      alpha: 0,
      duration: 250,
      ease: Phaser.Math.Easing.Cubic.Out,
      onComplete: () => {
        this.btnAudio.visible = false;
      }
    });
  }

  public showEndScreen(score: number, highScore: number, earnedPoints: number, earnedStars: number) {
    this.endScreen.visible = true;
    this.endScreen.activate(score, highScore, earnedPoints, earnedStars, this.gameScene.Level.hero);
  }

  public shutdown() {
    if (this?.gameScene?.scene?.isActive() || this?.gameScene?.scene?.isPaused()) this.gameScene.scene.stop();

    this.events.off(GameUI.events.Home, this.onHome, this);
    this.events.off(GameUI.events.LevelSelect, this.onLevelSelect, this);
    this.events.off(GameUI.events.Replay, this.onReplay, this);
    this.events.off(GameUI.events.NextLevel, this.onNextLevel, this);

    this.btnBack.off(UIButton.events.clicked, this.gameScene.back, this.gameScene);
    this.btnPause.off(UIButton.events.clicked, this.onPause, this);

    this.unbindGameEvents();

    super.shutdown();
  }

  protected finish() {}

  protected onHome() {
    app.gotoNextScene(this, TransitionPoint.GameLevelSelect, this.sceneData);
  }

  protected onLevelSelect() {
    app.gotoNextScene(this.gameScene, TransitionPoint.GameLevelSelect, this.sceneData);
  }

  protected onReplay() {
    app.gotoNextScene(this.gameScene, TransitionPoint.GameReplay, this.sceneData);
  }

  protected onNextLevel(levelName: LevelNameValues) {
    if (levelName) {
      if (!this.sceneData.runSettings) this.sceneData.runSettings = {};
      this.sceneData.runSettings.selectedLevel = levelName;
    }

    app.gotoNextScene(this.gameScene, TransitionPoint.GameReplay, this.sceneData);
  }

  protected onPause() {
    this.unbindOnPause();

    // Check if we're already moving.
    if (!this.isPausingOrResuming) {
      this.topBtnStartY = this.btnBack.y;
      // this.topBtnStartY = this.btnPause.y;
    } else {
      this.tweens.killTweensOf([this.btnBack, this.btnPause]);
    }

    this.isPausingOrResuming = true;

    this.add.tween({
      targets: [this.btnBack, this.btnPause],
      y: { from: this.topBtnStartY, to: -100 },
      duration: 600,
      ease: Phaser.Math.Easing.Back.Out
    });

    this.gameScene.pause();

    app.audioHandler.setChannelPause('sfx', true);
    app.audioHandler.setChannelPause('music', true);

    this.pauseMenu.visible = true;
    this.pauseMenu.activate(() => {
      this.onResume();
    });
  }

  protected onResume() {
    this.gameScene.resume();
    this.bindOnResume();

    app.audioHandler.setChannelPause('sfx', false);
    app.audioHandler.setChannelPause('music', false);

    this.add.tween({
      targets: [this.btnBack, this.btnPause],
      y: { from: -100, to: this.topBtnStartY },
      duration: 600,
      ease: Phaser.Math.Easing.Back.Out,
      onComplete: () => {
        this.isPausingOrResuming = false;
      }
    });
  }

  protected unbindOnPause() {
    this.btnBack.off(UIButton.events.clicked, this.gameScene.back, this.gameScene);
    this.btnPause.off(UIButton.events.clicked, this.onPause, this);
  }

  protected bindOnResume() {
    this.btnBack.on(UIButton.events.clicked, this.gameScene.back, this.gameScene);
    this.btnPause.on(UIButton.events.clicked, this.onPause, this);
  }

  // Pass through all game events we get to the game scene.
  protected bindGameEvents() {
    this.gameScene.events.on(RunManager.events.RunStarted, () => {});

    this.gameScene.events.on(RunManager.events.RunOver, () => {});
  }

  // Clean up any game events we've been passing to the game scene.
  protected unbindGameEvents() {
    this.gameScene.events.off(RunManager.events.RunStarted);
    this.gameScene.events.off(RunManager.events.RunOver);
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
